import { getTutorImageUrl } from "@/lib/school/tutorImages";
import { Tutor } from "@/types/tutor";
import { motion } from "framer-motion";
import {
  BookOpenCheck,
  Brain,
  MessageCircle,
  Play,
  Route,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import React from "react";

interface TutorCardProps {
  tutor: Tutor;
  onStart: (tutor: Tutor) => void;
  onDetails: (tutor: Tutor) => void;
  userRating?: number;
  onRate?: (tutorId: string, rating: number) => void;
  isRating?: boolean;
  featured?: boolean;
}

const archetypeLabel: Record<string, string> = {
  language_coach: "Language coach",
  exam_prep: "Exam prep",
  coding_mentor: "Coding mentor",
  roleplay_coach: "Roleplay coach",
  subject_tutor: "Subject tutor",
  interview_coach: "Interview coach",
  wellness_guide: "Wellness guide",
  custom: "Custom tutor",
};

const TutorCard: React.FC<TutorCardProps> = ({
  tutor,
  onStart,
  onDetails,
  userRating,
  onRate,
  isRating,
  featured = false,
}) => {
  const isZen = tutor.experience_type === "zen";
  const averageRating = Number.isFinite(tutor.rating_avg) ? tutor.rating_avg : 0;
  const totalRatings = Number.isFinite(tutor.rating_count) ? tutor.rating_count : 0;
  const hasJourney = Boolean(tutor.curriculum_count && tutor.curriculum_count > 0);
  const persona = tutor.archetype ? archetypeLabel[tutor.archetype] || tutor.archetype : "AI tutor";
  const tutorImageUrl = getTutorImageUrl(tutor);

  return (
    <motion.article
      whileHover={{ y: -2 }}
      className={`group relative flex h-full min-h-[380px] flex-col overflow-hidden rounded-lg border bg-[#15161B] text-white transition-colors ${
        featured ? "border-[#FD954F]/50" : "border-white/10 hover:border-white/22"
      }`}
    >
      <button
        type="button"
        onClick={() => onDetails(tutor)}
        className="flex flex-1 flex-col text-left"
        aria-label={`Open ${tutor.name} details`}
      >
        <div className="relative h-44 overflow-hidden bg-[#0F0F14]">
          <img
            src={tutorImageUrl}
            alt=""
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
            onError={(event) => {
              event.currentTarget.src = "/ui/Tutors/Phoebe.png";
            }}
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_44%,rgba(15,15,20,0.94))]" />
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/12 bg-black/55 text-[#FD954F]">
              {isZen ? <Sparkles size={17} /> : <MessageCircle size={17} />}
            </span>
            {hasJourney && (
              <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/12 bg-black/55 px-2.5 text-xs font-bold text-white/78">
                <Route size={13} />
                Journey
              </span>
            )}
          </div>
          <span className="absolute right-3 top-3 rounded-md border border-white/12 bg-black/60 px-2.5 py-1 text-[11px] font-bold text-white/78">
            {tutor.category}
          </span>
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-[#FD954F]">
                <Brain size={13} />
                {persona}
              </p>
              <h3 className="text-xl font-black leading-tight text-white">{tutor.name}</h3>
            </div>
            <div className="flex shrink-0 items-center gap-1 text-xs font-bold text-[#F6B66D]">
              <Star size={13} fill="currentColor" />
              {averageRating.toFixed(1)}
              <span className="font-semibold text-white/35">{totalRatings}</span>
            </div>
          </div>

          <p className="line-clamp-3 min-h-[63px] text-sm leading-5 text-white/62">
            {tutor.description || tutor.goal || "A focused AI tutor for guided practice."}
          </p>

          <div className="mt-auto flex items-center gap-4 border-t border-white/8 pt-4 text-xs font-bold text-white/52">
            <span className="inline-flex items-center gap-1.5">
              <BookOpenCheck size={13} className="text-[#FD954F]" />
              {isZen ? "Zen" : "Freeform"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users size={13} className="text-white/45" />
              {tutor.usage_count || 0} starts
            </span>
          </div>
        </div>
      </button>

      <div className="border-t border-white/8 bg-[#111216] p-4">
        <div className="mb-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onStart(tutor)}
            className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[#FD954F] text-sm font-black text-[#1C1C1E] hover:bg-[#F28747]"
          >
            <Play size={15} fill="currentColor" />
            Start
          </button>
        </div>

        <div
          className="flex items-center justify-between gap-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((starValue) => {
              const isFilled = (userRating || 0) >= starValue;
              return (
                <button
                  key={starValue}
                  type="button"
                  disabled={isRating}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRate?.(tutor.id, starValue);
                  }}
                  className="p-0.5 text-white/22 transition-colors hover:text-[#F59E0B] disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label={`Rate ${tutor.name} ${starValue} stars`}
                  title={`Rate ${starValue} star${starValue > 1 ? "s" : ""}`}
                >
                  <Star
                    size={14}
                    className={isFilled ? "text-[#F59E0B]" : "text-white/24"}
                    fill={isFilled ? "currentColor" : "none"}
                  />
                </button>
              );
            })}
          </div>
          <span className="text-[11px] font-semibold text-white/32">
            {userRating ? `Your rating: ${userRating}/5` : "Rate tutor"}
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export default TutorCard;
